import React from 'react';
import Icon from '../../../components/AppIcon';
import Badge from '../../../components/ui/Badge';

const CenterTable = ({ centers, onEdit, onDelete, onToggleStatus, onAssignUsers }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'onboarding':
        return 'bg-yellow-100 text-yellow-800';
      case 'maintenance':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Active',
      inactive: 'Inactive',
      onboarding: 'Onboarding',
      maintenance: 'Maintenance',
    };
    return labels?.[status] || status || '—';
  };

  const getCenterTypeLabel = (type) => {
    const labels = {
      coworking: 'Coworking',
      managed_office: 'Managed Office',
      furnished_office: 'Furnished Office',
      bareshell: 'Bareshell',
    };
    return labels?.[type] || type || '—';
  };

  const getCenterTypeColor = (type) => {
    switch (type) {
      case 'coworking': return 'bg-blue-100 text-blue-800';
      case 'managed_office': return 'bg-purple-100 text-purple-800';
      case 'furnished_office': return 'bg-green-100 text-green-800';
      case 'bareshell': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN')?.format(num || 0);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Center ID</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Sqft</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Seats</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Occupied</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Break-Even %</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Center Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {centers?.map((center) => (
            <tr key={center?.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{center?.id}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{center?.name}</div>
                <div className="text-sm text-gray-500">{center?.manager?.fullName || 'No Manager'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{center?.location}</div>
                <div className="text-sm text-gray-500">{center?.city}, {center?.state}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(center?.totalSqft)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(center?.totalSeats)}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{formatNumber(center?.occupiedSeats)}</div>
                <div className="text-xs text-gray-500">
                  {center?.totalSeats > 0 ? Math.round((center?.occupiedSeats / center?.totalSeats) * 100) : 0}% occupied
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-semibold text-gray-900">{center?.breakEvenPercentage || 0}%</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge className={getCenterTypeColor(center?.centerType || center?.inventoryType)}>
                  {getCenterTypeLabel(center?.centerType || center?.inventoryType)}
                </Badge>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Badge className={getStatusColor(center?.status)}>
                  {getStatusLabel(center?.status)}
                </Badge>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onEdit?.(center)}
                    className="text-blue-600 hover:text-blue-900 transition-colors"
                    title="Edit Center"
                  >
                    <Icon name="Edit" size={18} />
                  </button>
                  <button
                    onClick={() => onAssignUsers?.(center)}
                    className="text-green-600 hover:text-green-900 transition-colors"
                    title="Assign Users"
                  >
                    <Icon name="Users" size={18} />
                  </button>
                  <button
                    onClick={() => onToggleStatus?.(center?.id, center?.isActive)}
                    className="text-yellow-600 hover:text-yellow-900 transition-colors"
                    title={center?.isActive ? 'Deactivate' : 'Activate'}
                  >
                    <Icon name={center?.isActive ? 'ToggleRight' : 'ToggleLeft'} size={18} />
                  </button>
                  <button
                    onClick={() => onDelete?.(center?.id)}
                    className="text-red-600 hover:text-red-900 transition-colors"
                    title="Delete Center"
                  >
                    <Icon name="Trash2" size={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CenterTable;